import React from "react";
import styles from "./EnhancedCartSummary.module.css";

interface EnhancedCartSummaryProps {
  totalProducts: number;
  subtotal: number;
  totalDeliveryFee: number;
  promotionalDiscount: number;
  couponDiscount: number;
  appliedCouponCode: string | null;
  total: number;
  hasLocationSet: boolean;
  isUpdatingFees: boolean;
  allGroupsHaveDeliveryFees: boolean;
  // Phase 2.3: Pricing loading state
  isPricingLoading?: boolean;
  onCheckout: () => void;
  isCheckoutDisabled: boolean;
}

export const EnhancedCartSummary: React.FC<EnhancedCartSummaryProps> = ({
  totalProducts,
  subtotal,
  totalDeliveryFee,
  promotionalDiscount,
  couponDiscount,
  appliedCouponCode,
  total,
  hasLocationSet,
  isUpdatingFees,
  allGroupsHaveDeliveryFees,
  // Phase 2.3: Pricing loading state
  isPricingLoading = false,
  onCheckout,
  isCheckoutDisabled,
}) => {
  const totalSavings = promotionalDiscount + couponDiscount;
  const hasDiscounts = totalSavings > 0;

  return (
    <div className={styles.summaryContainer}>
      <div className={styles.summaryHeader}>
        <h2>Resumen de Pedido</h2>
      </div>

      <div className={styles.summaryContent}>
        {/* Products Subtotal */}
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>
            Subtotal ({totalProducts}{" "}
            {totalProducts === 1 ? "producto" : "productos"})
          </span>
          <span className={styles.summaryValue}>
            {isPricingLoading ? (
              <span className={styles.loadingFee}>
                <span className={styles.spinningIcon}></span>
                Calculando
              </span>
            ) : (
              `S/ ${subtotal.toFixed(2)}`
            )}
          </span>
        </div>

        {/* Shipping */}
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>📦 Costo de envío</span>
          <span className={styles.summaryValue}>
            {!hasLocationSet ? (
              "Por calcular"
            ) : isUpdatingFees ? (
              <span className={styles.loadingFee}>
                <span className={styles.spinningIcon}></span>
                Calculando
              </span>
            ) : !allGroupsHaveDeliveryFees ? (
              "Por calcular"
            ) : totalDeliveryFee > 0 ? (
              `S/ ${totalDeliveryFee.toFixed(2)}`
            ) : (
              <span className={styles.freeShipping}>Gratis</span>
            )}
          </span>
        </div>

        {/* Discounts Section */}
        {hasDiscounts && (
          <div className={styles.discountsSection}>
            <div className={styles.discountsSectionHeader}>
              <span className={styles.discountsTitle}>
                🏷️ Descuentos aplicados
              </span>
            </div>

            {/* Promotional Discounts */}
            {promotionalDiscount > 0 && (
              <div className={styles.discountRow}>
                <span className={styles.discountLabel}>
                  • Descuentos automáticos
                </span>
                <span className={styles.discountValue}>
                  -S/ {promotionalDiscount.toFixed(2)}
                </span>
              </div>
            )}

            {/* Coupon Discounts */}
            {appliedCouponCode && couponDiscount > 0 && (
              <div className={styles.discountRow}>
                <span className={styles.discountLabel}>
                  • Cupón {appliedCouponCode}
                </span>
                <span className={styles.discountValue}>
                  -S/ {couponDiscount.toFixed(2)}
                </span>
              </div>
            )}

            {/* Total Savings */}
            <div className={styles.totalSavingsRow}>
              <span className={styles.totalSavingsLabel}>
                💰 Total ahorrado:
              </span>
              <span className={styles.totalSavingsValue}>
                S/ {totalSavings.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Final Total */}
        <div className={`${styles.summaryRow} ${styles.totalRow}`}>
          <span className={styles.totalLabel}>💵 TOTAL A PAGAR</span>
          <span className={styles.totalValue}>
            {!hasLocationSet ||
            isUpdatingFees ||
            !allGroupsHaveDeliveryFees ||
            isPricingLoading ? (
              <span className={styles.calculatingTotal}>
                {isUpdatingFees || isPricingLoading ? (
                  <>
                    <span className={styles.spinningIcon}></span>
                    Calculando
                  </>
                ) : (
                  "Por calcular"
                )}
              </span>
            ) : (
              <span className={styles.finalPrice}>S/ {total.toFixed(2)}</span>
            )}
          </span>
        </div>
      </div>

      <div className={styles.summaryFooter}>
        <button
          onClick={onCheckout}
          className={`buttonMain ${isCheckoutDisabled ? "disabled" : ""}`}
          disabled={isCheckoutDisabled}
        >
          Continuar con el pago
        </button>
        <p className={styles.termsText}>
          Al continuar, aceptas nuestros términos y condiciones
        </p>
      </div>
    </div>
  );
};

export default EnhancedCartSummary;
